import React, { useMemo } from "react";
import { Box, Chip, IconButton, Tooltip, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { DataGrid } from "@mui/x-data-grid";
import { generateRandomColor } from "utils/generateRandomColor";
import AvatarRandomColor from "pages/OrgHistory/components/ReusableDataGrid/AvatarRandomColor";

/**
 * Format date to a readable format
 */
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

/**
 * InactiveUsersTable - DataGrid component for displaying inactive users
 */
const InactiveUsersTable = ({ 
  users = [], 
  showCountryColumn = false,
  isLoading = false,
  onUserClick 
}) => {
  // Column definitions
  const columns = useMemo(() => {
    const baseColumns = [
      {
        field: "profile",
        headerName: "Profile",
        headerAlign: "center",
        align: "center",
        width: 80,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Tooltip title="View user details">
            <IconButton
              size="small"
              aria-label="View user details"
              onClick={() => onUserClick && onUserClick(params.row)}
              sx={{ p: 0.25 }}
            >
              <InfoOutlinedIcon sx={{ color: "#0084ff", fontSize: 22 }} />
            </IconButton>
          </Tooltip>
        ),
      },
      {
        field: "full_name",
        headerName: "Name",
        flex: 1,
        minWidth: 300,
        renderCell: (params) => {
          const email = params.row.email || "";
          const fullName = params.row.full_name || "";
          const userId = params.row.id || "";
          const color = generateRandomColor(userId);
          
          let firstName = "";
          let lastName = "";
          
          // Parse full_name if available (format: "LastName, FirstName")
          if (fullName) {
            const nameParts = fullName.split(",").map(part => part.trim());
            if (nameParts.length === 2) {
              lastName = nameParts[0];
              firstName = nameParts[1];
            } else {
              // If comma not found, use the whole name as firstName
              firstName = fullName;
            }
          } else if (email) {
            // Fallback: Extract from email if full_name is not available
            const emailParts = email.split("@")[0].split(/[._-]/);
            firstName = emailParts[0] || "";
            lastName = emailParts[1] || emailParts[0] || "";
          }
          
          // Display name (use full_name as is, or construct from email)
          const displayName = fullName || email.split("@")[0];
          
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <AvatarRandomColor
                firstName={firstName}
                lastName={lastName}
                color={color}
              />
              <Box sx={{ overflow: "hidden", display: "flex", flexDirection: "column" }}>
                <Tooltip title={displayName} placement="top" arrow>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 600, 
                      fontSize: "0.9rem",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "220px"
                    }}
                  >
                    {displayName}
                  </Typography>
                </Tooltip>
                <Tooltip title={email} placement="bottom" arrow>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontSize: "0.8rem",
                      color: "#666",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "220px"
                    }}
                  >
                    {email}
                  </Typography>
                </Tooltip>
              </Box>
            </Box>
          );
        },
      },
    ];

    // Add country column only if needed
    if (showCountryColumn) {
      baseColumns.push({
        field: "country",
        headerName: "Country",
        flex: 1,
        minWidth: 180,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <Typography 
            variant="body2" 
            sx={{ 
              fontSize: "0.875rem",
              color: "#555",
              fontWeight: 500
            }}
          >
            {params.row.country || "N/A"}
          </Typography>
        ),
      });
    }

    baseColumns.push(
      {
        field: "status",
        headerName: "Status",
        width: 130,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => {
          const status = params.row.status?.toLowerCase();
          return (
            <Chip
              label={params.row.status || "N/A"}
              size="small"
              sx={{
                backgroundColor:
                  status === "active"
                    ? "#e8f5e9"
                    : status === "inactive"
                    ? "#ffebee"
                    : "#f5f5f5",
                color:
                  status === "active"
                    ? "#2e7d32"
                    : status === "inactive"
                    ? "#c62828"
                    : "#757575",
                fontWeight: 600,
                fontSize: "0.8rem",
                textTransform: "capitalize",
                minWidth: "80px"
              }}
            />
          );
        },
      },
      {
        field: "updated_at",
        headerName: "Last Active At",
        flex: 1,
        minWidth: 160,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <Typography 
            variant="body2" 
            sx={{ 
              color: "#555", 
              fontSize: "0.875rem",
              fontWeight: 500 
            }}
          >
            {formatDate(params.row.updated_at)}
          </Typography>
        ),
      }
    );

    return baseColumns;
  }, [showCountryColumn, onUserClick]);

  // Prepare rows for DataGrid
  const rows = useMemo(() => {
    return users.map((user) => ({
      id: user._id,
      email: user.email,
      full_name: user.full_name,
      first_name: user.first_name,
      last_name: user.last_name,
      user_name: user.user_name,
      country: user.country,
      status: user.status,
      updated_at: user.updated_at,
    }));
  }, [users]);

  return (
    <Box
      sx={{
        height: 600,
        width: "100%",
        marginTop: "24px",
        "& .MuiDataGrid-root": {
          border: "1px solid #e0e0e0",
          borderRadius: "8px",
          fontSize: "0.875rem",
        },
        "& .MuiDataGrid-columnHeaders": {
          backgroundColor: "#f8f9fa",
          borderBottom: "2px solid #e0e0e0",
          fontWeight: 600,
          fontSize: "0.85rem",
          minHeight: "52px !important",
          maxHeight: "52px !important",
        },
        "& .MuiDataGrid-columnHeaderTitle": {
          fontWeight: 600,
        },
        "& .MuiDataGrid-cell": {
          borderBottom: "1px solid #f0f0f0",
          padding: "14px 16px",
        },
        "& .MuiDataGrid-row": {
          minHeight: "58px !important",
          maxHeight: "58px !important",
        },
        "& .MuiDataGrid-row:hover": {
          backgroundColor: "#f8f9fa",
        },
        "& .MuiDataGrid-virtualScroller": {
          minHeight: "450px",
        },
      }}
    >
      <DataGrid
        rows={rows}
        columns={columns}
        loading={isLoading}
        disableRowSelectionOnClick
        disableColumnMenu
        hideFooter
        rowHeight={58}
        localeText={{
          noRowsLabel: "No inactive users found",
        }}
        sx={{
          "& .MuiDataGrid-overlayWrapper": {
            minHeight: "450px",
          },
        }}
      />
    </Box>
  );
};

export default InactiveUsersTable;
